import React, { useState } from 'react';
import { CheckCircle, ArrowRight, Sparkles } from 'lucide-react';

const TargetSelectionPage = ({ onSelectTarget, saving }) => {
  const [selectedTarget, setSelectedTarget] = useState(null);

  const targets = [
    {
      id: 'quickbooks',
      name: 'QuickBooks',
      icon: '💚',
      color: 'green',
      description: 'Export invoices in QuickBooks-compatible CSV format',
      features: [
        'Direct CSV import to QuickBooks',
        'All required fields mapped',
        'Vendor and customer data included',
        'Line items with tax calculations'
      ],
      gradient: 'from-green-500 to-emerald-600'
    },
    {
      id: 'coupa',
      name: 'Coupa',
      icon: '💙',
      color: 'indigo',
      description: 'Export invoices in Coupa-compatible format (CSV/JSON)',
      features: [
        'Coupa CSV or JSON format',
        'Supplier and buyer information',
        'Line-level taxation support',
        'Custom fields included'
      ],
      gradient: 'from-indigo-500 to-purple-600'
    }
  ];

  const handleContinue = () => {
    if (selectedTarget) {
      onSelectTarget(selectedTarget);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-6">
      <div className="max-w-5xl w-full">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full mb-4">
            <Sparkles className="w-5 h-5" />
            <span className="font-semibold">Welcome! Let's Get Started</span>
          </div>
          <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            Choose Your Accounting System
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Select the system you use for invoice processing. This is a one-time setup and cannot be changed later.
          </p>
        </div>

        {/* Target Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {targets.map((target) => (
            <button
              key={target.id}
              onClick={() => setSelectedTarget(target.id)}
              className={`relative group text-left transition-all duration-300 ${
                selectedTarget === target.id
                  ? 'scale-105'
                  : 'hover:scale-102'
              }`}
            >
              {/* Glow effect */}
              <div className={`absolute inset-0 bg-gradient-to-r ${target.gradient} rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity ${
                selectedTarget === target.id ? 'opacity-30' : ''
              }`}></div>

              {/* Card */}
              <div className={`relative bg-white rounded-3xl p-8 shadow-lg border-4 transition-all ${
                selectedTarget === target.id
                  ? `border-${target.color}-500`
                  : 'border-gray-200 group-hover:border-gray-300'
              }`}>
                {/* Selected Indicator */}
                {selectedTarget === target.id && (
                  <div className={`absolute -top-3 -right-3 bg-gradient-to-r ${target.gradient} text-white rounded-full p-2 shadow-lg`}>
                    <CheckCircle className="w-6 h-6" />
                  </div>
                )}

                {/* Icon & Title */}
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-16 h-16 bg-gradient-to-br from-${target.color}-100 to-${target.color}-200 rounded-2xl flex items-center justify-center text-4xl`}>
                    {target.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{target.name}</h3>
                    <p className="text-sm text-gray-500">Accounting System</p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 mb-6">
                  {target.description}
                </p>

                {/* Features */}
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Key Features:</p>
                  {target.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <div className={`bg-${target.color}-100 rounded-full p-1 mt-0.5 flex-shrink-0`}>
                        <CheckCircle className={`w-3.5 h-3.5 text-${target.color}-600`} />
                      </div>
                      <p className="text-sm text-gray-700">{feature}</p>
                    </div>
                  ))}
                </div>

                {/* Selected Badge */}
                {selectedTarget === target.id && (
                  <div className={`mt-6 px-4 py-2 bg-gradient-to-r ${target.gradient} text-white rounded-lg text-center font-semibold`}>
                    ✓ Selected
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Continue Button */}
        <div className="text-center">
          <button
            onClick={handleContinue}
            disabled={!selectedTarget || saving}
            className={`inline-flex items-center gap-3 px-10 py-4 rounded-2xl font-bold text-lg transition-all shadow-lg ${
              selectedTarget && !saving
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-2xl hover:scale-105'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Saving Selection...</span>
              </>
            ) : (
              <>
                <span>Continue to Dashboard</span>
                <ArrowRight className="w-6 h-6" />
              </>
            )}
          </button>

          {!selectedTarget && (
            <p className="text-sm text-gray-500 mt-4">
              Please select a system to continue
            </p>
          )}
        </div>

        {/* Warning Notice */}
        <div className="mt-8 max-w-2xl mx-auto p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-sm text-amber-800 text-center">
            <strong>⚠️ Important:</strong> This selection is permanent and cannot be changed. Choose carefully!
          </p>
        </div>
      </div>
    </div>
  );
};

export default TargetSelectionPage;